import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  XCircle,
  X,
  ExternalLink,
} from '@/utils/icons';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { getHealthScoreColor } from '@/utils/calculations';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/utils/formatters';
import type { FleetSummary, SingleMetricStats } from '@/types/metrics';
import { PeriodStatsRow } from '@/components/common/PeriodStatsRow';

export type DrillMetric = 'uptime' | 'version' | 'ntp';

interface FleetHealthScoreProps {
  score: number;
  summary?: FleetSummary;
  expanded?: boolean;
  onToggleExpand?: () => void;
  drillMetric?: DrillMetric | null;
  onDrillMetricChange?: (metric: DrillMetric | null) => void;
  periodStats?: SingleMetricStats;
  periodStatsLoading?: boolean;
}

function ScoreBar({
  label,
  score,
  weight,
  description,
  failCount,
  onClick,
}: {
  label: string;
  score: number;
  weight: string;
  description?: string;
  failCount?: number;
  onClick?: () => void;
}) {
  const pct = Math.min(100, Math.max(0, score));
  const barColor = pct >= 95 ? 'dls-bg-status-online' : pct >= 80 ? 'dls-bg-status-warning' : 'dls-bg-status-offline';
  const Icon = pct >= 95 ? CheckCircle : pct >= 80 ? AlertTriangle : XCircle;
  const iconColor = pct >= 95 ? 'dls-text-status-online' : pct >= 80 ? 'dls-text-status-warning' : 'dls-text-status-offline';
  const clickable = onClick && failCount && failCount > 0;

  return (
    <button
      type="button"
      onClick={clickable ? onClick : undefined}
      className={cn(
        'dls-w-full dls-text-left dls-space-y-1 dls-rounded-md dls-px-2 dls-py-1.5 -dls-mx-2 dls-transition-colors',
        clickable ? 'hover:dls-bg-muted/60 dls-cursor-pointer' : 'dls-cursor-default'
      )}
    >
      <div className="dls-flex dls-items-center dls-justify-between dls-text-xs dls-gap-2">
        <div className="dls-flex dls-items-center dls-gap-1.5 dls-shrink-0">
          <Icon className={cn('dls-h-3.5 dls-w-3.5 dls-shrink-0', iconColor)} />
          <span className="dls-font-medium">{label}</span>
        </div>
        <div className="dls-flex dls-items-center dls-gap-2">
          <span className="dls-text-muted-foreground dls-text-[11px] dls-shrink-0">{weight}</span>
          <span className="dls-font-medium dls-shrink-0">{pct.toFixed(1)}%</span>
          {clickable ? <ChevronRight className="dls-h-3 dls-w-3 dls-text-muted-foreground dls-shrink-0" /> : null}
        </div>
      </div>
      <div className="dls-h-1.5 dls-rounded-full dls-bg-muted dls-overflow-hidden">
        <div
          className={cn('dls-h-full dls-rounded-full dls-transition-all', barColor)}
          style={{ width: `${pct}%` }}
        />
      </div>
      {description && (
        <p className="dls-text-[11px] dls-text-muted-foreground dls-leading-tight">{description}</p>
      )}
    </button>
  );
}

function VersionComplianceSummary({ summary }: { summary: FleetSummary }) {
  const { compliant, outdated, latestVersion } = summary.versionCompliance;
  const total = summary.totalNodes;
  const pct = total > 0 ? (compliant / total) * 100 : 100;

  return (
    <div className="dls-space-y-3 dls-mb-6 dls-pb-6 dls-border-b">
      {latestVersion && (
        <p className="dls-text-sm dls-text-muted-foreground">
          Latest version: <span className="dls-font-mono dls-font-medium">{latestVersion}</span>
        </p>
      )}
      <div className="dls-flex dls-items-center dls-justify-between">
        <div className="dls-flex dls-items-center dls-gap-2 dls-text-status-online">
          <CheckCircle className="dls-h-5 dls-w-5" />
          <span className="dls-font-medium">{compliant} up to date</span>
        </div>
        <span className="dls-text-2xl dls-font-bold dls-text-status-online">{pct.toFixed(0)}%</span>
      </div>
      {outdated > 0 && (
        <div className="dls-flex dls-items-center dls-gap-2 dls-text-status-warning">
          <XCircle className="dls-h-5 dls-w-5" />
          <span className="dls-font-medium">{outdated} need upgrade</span>
        </div>

      )}
      <div className="dls-w-full dls-bg-muted dls-rounded-full dls-h-2">
        <div
          className="dls-bg-status-online dls-h-2 dls-rounded-full dls-transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function DrillDrawer({
  metric,
  summary,
  onClose,
}: {
  metric: DrillMetric;
  summary: FleetSummary;
  onClose: () => void;
}) {
  const config = {
    uptime: {
      title: 'Unreachable Nodes',
      subtitle: `${summary.unreachableNodes} Node${summary.unreachableNodes !== 1 ? 's' : ''} not in ALIVE state`,
      nodes:
        summary.unreachableNodeList?.map((n) => ({
          id: n.id,
          name: n.name,
          detail: n.state,
          portfolio: n.portfolio,
        })) ?? [],
    },
    version: {
      title: 'Outdated Software',
      subtitle: `${summary.versionCompliance.outdated} Node${summary.versionCompliance.outdated !== 1 ? 's' : ''} not on ${summary.versionCompliance.latestVersion ?? 'latest'}`,
      nodes:
        summary.outdatedNodeList?.map((n) => ({
          id: n.id,
          name: n.name,
          detail: n.version ?? 'Unknown',
          portfolio: n.portfolio,
        })) ?? [],
    },
    ntp: {
      title: 'NTP Sync Issues',
      subtitle: `${summary.ntpFailingCount} Node${summary.ntpFailingCount !== 1 ? 's' : ''} not synchronized`,
      nodes:
        summary.ntpFailingNodeList?.map((n) => ({
          id: n.id,
          name: n.name,
          detail: 'NTP not synchronized',
          portfolio: n.portfolio,
        })) ?? [],
    },
  }[metric];

  // Also include stale + flapping for uptime drill-down
  const staleNodes =
    metric === 'uptime'
      ? (summary.staleNodeList?.map((n) => ({
          id: n.id,
          name: n.name,
          detail: n.lastContact ? `Last seen ${formatTimeAgo(n.lastContact)}` : 'No contact',
          portfolio: n.portfolio,
        })) ?? [])
      : [];

  const flappingNodes =
    metric === 'uptime'
      ? summary.flappingNodes.map((n) => ({
          id: n.nodeId,
          name: n.nodeName,
          detail: `${n.stateChangeCount} state changes (24h)`,
          portfolio: null as string | null,
        }))
      : [];

  return (
    <>
      <div className="dls-fixed dls-inset-0 dls-bg-white/30 dls-backdrop-blur-sm dls-z-40" onClick={onClose} />
      <div className="dls-fixed dls-right-0 dls-top-0 dls-bottom-0 dls-w-full dls-max-w-lg dls-bg-background dls-border-l dls-shadow-50 dls-z-50 dls-flex dls-flex-col">
        <div className="dls-flex dls-items-center dls-justify-between dls-px-6 dls-py-4 dls-border-b">
          <div>
            <h2 className="dls-text-lg dls-font-semibold">{config.title}</h2>
            <p className="dls-text-sm dls-text-muted-foreground">{config.subtitle}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="dls-h-5 dls-w-5" />
          </Button>
        </div>

        <div className="dls-flex-1 dls-overflow-y-auto dls-p-6">
          {metric === 'version' && <VersionComplianceSummary summary={summary} />}
          {config.nodes.length === 0 && staleNodes.length === 0 && flappingNodes.length === 0 ? (
            <div className="dls-flex dls-items-center dls-justify-center dls-h-full dls-text-muted-foreground">
              <div className="dls-text-center">
                <CheckCircle className="dls-h-8 dls-w-8 dls-mx-auto dls-mb-2 dls-text-status-online" />
                <p>No issues found</p>
              </div>
            </div>
          ) : (
            <div className="dls-space-y-6">
              {config.nodes.length > 0 && (
                <div className="dls-space-y-2">
                  {metric === 'uptime' && (staleNodes.length > 0 || flappingNodes.length > 0) && (
                    <h3 className="dls-text-sm dls-font-medium dls-text-muted-foreground">Unreachable</h3>
                  )}
                  {config.nodes.map((node) => (
                    <div
                      key={node.id}
                      className="dls-flex dls-items-center dls-justify-between dls-p-3 dls-border dls-rounded-lg hover:dls-bg-muted/50 dls-transition-colors"
                    >
                      <div className="dls-space-y-0.5">
                        <Link
                          to={`/nodes/${node.id}`}
                          className="dls-font-medium dls-text-sm hover:dls-underline dls-flex dls-items-center dls-gap-1"
                        >
                          {node.name}
                          <ExternalLink className="dls-h-3 dls-w-3 dls-text-muted-foreground" />
                        </Link>
                        <p className="dls-text-xs dls-text-muted-foreground">
                          {node.detail}
                          {node.portfolio ? ` · ${node.portfolio}` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {staleNodes.length > 0 && (
                <div className="dls-space-y-2">
                  <h3 className="dls-text-sm dls-font-medium dls-text-muted-foreground">Stale</h3>
                  {staleNodes.map((node) => (
                    <div
                      key={node.id}
                      className="dls-flex dls-items-center dls-justify-between dls-p-3 dls-border dls-rounded-lg hover:dls-bg-muted/50 dls-transition-colors"
                    >
                      <div className="dls-space-y-0.5">
                        <Link
                          to={`/nodes/${node.id}`}
                          className="dls-font-medium dls-text-sm hover:dls-underline dls-flex dls-items-center dls-gap-1"
                        >
                          {node.name}
                          <ExternalLink className="dls-h-3 dls-w-3 dls-text-muted-foreground" />
                        </Link>
                        <p className="dls-text-xs dls-text-muted-foreground">
                          {node.detail}
                          {node.portfolio ? ` · ${node.portfolio}` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {flappingNodes.length > 0 && (
                <div className="dls-space-y-2">
                  <h3 className="dls-text-sm dls-font-medium dls-text-muted-foreground">Flapping</h3>
                  {flappingNodes.map((node) => (
                    <div
                      key={node.id}
                      className="dls-flex dls-items-center dls-justify-between dls-p-3 dls-border dls-rounded-lg hover:dls-bg-muted/50 dls-transition-colors"
                    >
                      <div className="dls-space-y-0.5">
                        <Link
                          to={`/nodes/${node.id}`}
                          className="dls-font-medium dls-text-sm hover:dls-underline dls-flex dls-items-center dls-gap-1"
                        >
                          {node.name}
                          <ExternalLink className="dls-h-3 dls-w-3 dls-text-muted-foreground" />
                        </Link>
                        <p className="dls-text-xs dls-text-muted-foreground">{node.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function FleetHealthScore({
  score,
  summary,
  expanded: controlledExpanded,
  onToggleExpand,
  drillMetric: controlledDrill,
  onDrillMetricChange,
  periodStats,
  periodStatsLoading,
}: FleetHealthScoreProps) {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = controlledExpanded ?? internalExpanded;
  const toggleExpand = onToggleExpand ?? (() => setInternalExpanded(!internalExpanded));
  const [internalDrill, setInternalDrill] = useState<DrillMetric | null>(null);
  const drillMetric = controlledDrill !== undefined ? controlledDrill : internalDrill;
  const setDrillMetric = onDrillMetricChange ?? setInternalDrill;
  const colorClass = getHealthScoreColor(score);

  const uptimePct = summary
    ? summary.totalNodes > 0
      ? (summary.aliveNodes / summary.totalNodes) * 100
      : 100
    : null;
  const versionPct = summary
    ? summary.totalNodes > 0
      ? (summary.versionCompliance.compliant / summary.totalNodes) * 100
      : 100
    : null;
  const ntpPct = summary?.ntpSyncPercent ?? 100;

  const uptimeFailCount = summary
    ? summary.unreachableNodes + summary.staleNodes + summary.flappingNodes.length
    : 0;
  const versionFailCount = summary?.versionCompliance.outdated ?? 0;

  // Build actionable recommendations
  const actions: string[] = [];
  if (summary) {
    if (summary.unreachableNodes > 0) {
      actions.push(
        `${summary.unreachableNodes} Node${summary.unreachableNodes > 1 ? 's' : ''} unreachable — check connectivity and power status`
      );
    }
    if (summary.versionCompliance.outdated > 0) {
      const ver = summary.versionCompliance.latestVersion ?? 'latest';
      actions.push(
        `${summary.versionCompliance.outdated} Node${summary.versionCompliance.outdated > 1 ? 's' : ''} on outdated software — upgrade to ${ver}`
      );
    }
    if (summary.flappingNodes.length > 0) {
      actions.push(
        `${summary.flappingNodes.length} Node${summary.flappingNodes.length > 1 ? 's' : ''} flapping (3+ state changes in 24h) — investigate stability`
      );
    }
    if (summary.staleNodes > 0) {
      actions.push(
        `${summary.staleNodes} stale Node${summary.staleNodes > 1 ? 's' : ''} — not reporting within expected interval`
      );
    }
    if (summary.ntpFailingCount > 0) {
      actions.push(
        `${summary.ntpFailingCount} Node${summary.ntpFailingCount > 1 ? 's' : ''} failing NTP sync — check NTP server configuration`
      );
    }
  }

  return (
    <>
      <Card>
        <CardHeader className="dls-px-4 dls-pb-2">
          <CardTitle className="dls-text-sm dls-font-medium dls-text-muted-foreground dls-flex dls-items-center dls-gap-1.5 dls-whitespace-nowrap">
            Health Score
            <InfoTooltip text="Composite score (0-100) based on uptime (80%), version compliance (15%), and NTP sync (5%). Click to expand breakdown." />
            {summary && (
              <button
                type="button"
                className="dls-ml-auto dls-text-muted-foreground hover:dls-text-foreground dls-transition-colors"
                onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
              >
                {expanded ? <ChevronUp className="dls-h-4 dls-w-4" /> : <ChevronDown className="dls-h-4 dls-w-4" />}
              </button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="dls-px-4 dls-pb-3">
          <div
            role={summary ? 'button' : undefined}
            tabIndex={summary ? 0 : undefined}
            onClick={summary ? () => toggleExpand() : undefined}
            onKeyDown={
              summary
                ? (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleExpand();
                    }
                  }
                : undefined
            }
            className={cn(
              'dls-w-full dls-text-left dls-min-h-20',
              summary ? 'dls-cursor-pointer' : 'dls-cursor-default'
            )}
          >
            <div>
              <span className={cn('dls-text-4xl dls-font-bold', colorClass)}>{score.toFixed(1)}</span>
              <span className="dls-text-lg dls-text-muted-foreground dls-font-normal dls-ml-0.5">/ 100</span>
              {periodStats && (
                <div className="dls-mt-1">
                  <PeriodStatsRow
                    stats={periodStats}
                    format={(v) => v.toFixed(1)}
                    loading={periodStatsLoading}
                  />
                </div>
              )}
            </div>
          </div>

          {expanded && summary && (
            <div className="dls-mt-3 dls-pt-3 dls-border-t dls-space-y-1 dls-max-h-[300px] dls-overflow-y-auto">
              {/* Score breakdown */}
              {uptimePct !== null && (
                <ScoreBar
                  label="Uptime"
                  score={uptimePct}
                  weight="80%"
                  description={`${summary.aliveNodes} of ${summary.totalNodes} Nodes online`}
                  failCount={uptimeFailCount}
                  onClick={() => setDrillMetric('uptime')}
                />
              )}
              {versionPct !== null && (
                <ScoreBar
                  label="Version"
                  score={versionPct}
                  weight="15%"
                  description={`${summary.versionCompliance.compliant} of ${summary.totalNodes} on ${summary.versionCompliance.latestVersion ?? 'latest'}`}
                  failCount={versionFailCount}
                  onClick={() => setDrillMetric('version')}
                />
              )}
              <ScoreBar
                label="NTP Sync"
                score={ntpPct}
                weight="5%"
                description={
                  summary.ntpFailingCount > 0
                    ? `${summary.ntpFailingCount} Node${summary.ntpFailingCount !== 1 ? 's' : ''} not synchronized`
                    : undefined
                }
                failCount={summary.ntpFailingCount}
                onClick={() => setDrillMetric('ntp')}
              />

              {/* Actions */}
              {actions.length > 0 && (
                <div className="dls-pt-2 dls-border-t dls-mt-2">
                  <p className="dls-text-xs dls-font-medium dls-mb-1.5">Recommended Actions</p>
                  <ul className="dls-space-y-1">
                    {actions.map((action, i) => (
                      <li
                        key={i}
                        className="dls-flex dls-items-start dls-gap-1.5 dls-text-[11px] dls-text-muted-foreground dls-leading-tight"
                      >
                        <span className="dls-text-status-warning dls-mt-0.5 dls-shrink-0">•</span>
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {actions.length === 0 && (
                <div className="dls-pt-2 dls-border-t dls-mt-2">
                  <p className="dls-text-xs dls-text-status-online dls-flex dls-items-center dls-gap-1.5">
                    <CheckCircle className="dls-h-3.5 dls-w-3.5" />
                    All systems healthy — no action needed
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {drillMetric && summary && (
        <DrillDrawer metric={drillMetric} summary={summary} onClose={() => setDrillMetric(null)} />
      )}
    </>
  );
}
